import { Service } from "@modules/core/services/serviceType"
import { ItemProgramInfiniteSession, ItemProgramOnOffSession } from "@modules/programSession/programSessionTypes"
import { ProgramInfiniteSessionApi } from "@modules/programSession/services/programInfiniteSessionApi"
import { ProgramOnOffSessionApi } from "@modules/programSession/services/programOnOffSessionApi"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramTypeOf } from "@modules/programs/programTypes"
import { ProgramApiGateway } from "@modules/programs/services/programApi.gateway"
import { ProgramInfiniteApi } from "@modules/programs/services/programInfiniteApi"
import { ProgramOnOffApi } from "@modules/programs/services/programOnOffApi"
import { toggleInArray } from "@modules/utils/objectUtils"
import { observable } from "micro-observables"

export class PlanningService implements Service {
  private _planningSessions = observable<ItemProgramInfiniteSession[] | ItemProgramOnOffSession[]>([])
  readonly planningSessions = this._planningSessions.readOnly()
  private _isLoadingSessions = observable<boolean>(false)
  readonly isLoadingSessions = this._isLoadingSessions.readOnly()
  private _planningSessionsParams = observable<{
    startDate: string | number
    endDate: string | number
    programId?: string
    programType?: string
  }>({ startDate: "", endDate: "" })

  private _isLoadingPrograms = observable<boolean>(true)
  readonly isLoadingPrograms = this._isLoadingPrograms.readOnly()
  private _filteredProgramPlanning = observable<string[]>([])
  readonly filteredProgramPlanning = this._filteredProgramPlanning.readOnly()

  constructor(
    private programApi: ProgramApiGateway,
    private programInfiniteApi: ProgramInfiniteApi,
    private programOnOffApi: ProgramOnOffApi,
    private programInfiniteSessionApi: ProgramInfiniteSessionApi,
    private programOnOffSessionApi: ProgramOnOffSessionApi,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async init(): Promise<void> {}

  async onLoggedOut(): Promise<void> {
    this._planningSessions.set([])
    this._filteredProgramPlanning.set([])
    this._planningSessionsParams.set({ startDate: "", endDate: "" })
  }

  async updateProgramDatedSessions(
    startDate: string,
    endDate: string,
    programId?: string,
    programType?: ProgramTypeOf,
  ) {
    if (!programId) {
      this._planningSessions.set([])
      return
    }

    this._isLoadingSessions.set(true)
    this._planningSessionsParams.set({ startDate, endDate, programId, programType })

    if (programType === ProgramOnOffTypeOf) {
      const result = await this.programOnOffSessionApi.getSessions(startDate, endDate, programId)

      this._planningSessions.set(result)
    } else {
      const result = await this.programInfiniteSessionApi.getSessions(startDate, endDate, programId)

      this._planningSessions.set(result)
    }
    this._isLoadingSessions.set(false)
  }

  async updateProgramUnDatedSessions(startIndex: number, endIndex: number, programId: string) {
    this._isLoadingSessions.set(true)
    this._planningSessionsParams.set({ startDate: startIndex, endDate: endIndex, programId, programType: undefined })
    const result = await this.programOnOffSessionApi.getSessionsByIndex(startIndex, endIndex, programId)
    this._planningSessions.set(result)
    this._isLoadingSessions.set(false)
  }

  async refreshSessions() {
    const { startDate, endDate, programId, programType } = this._planningSessionsParams.get()

    if (typeof startDate === "number" && typeof endDate === "number" && programId) {
      this.updateProgramUnDatedSessions(startDate, endDate, programId)
    } else {
      this.updateProgramDatedSessions(startDate as string, endDate as string, programId, programType as ProgramTypeOf)
    }
  }

  async initFilteredPrograms() {
    if (this._filteredProgramPlanning.get().length === 0) {
      this._isLoadingPrograms.set(true)

      // Idealy, we should use a single endpoint to get all programs - backend doesn't return SHARED_COACH programs yet using ALL filter
      const infinitePrograms = await this.programInfiniteApi.getInfinitePrograms("ALL", "SUBSCRIPTION_DATE")
      const onOffPrograms = await this.programOnOffApi.getOnOffPrograms("ALL", "SUBSCRIPTION_DATE")
      const sharedCoachPrograms = await this.programApi.getPrograms("SHARED_COACH", "SUBSCRIPTION_DATE")

      this._filteredProgramPlanning.set([
        ...infinitePrograms.content.map((program) => program.id),
        ...onOffPrograms.content.map((program) => program.id),
        ...sharedCoachPrograms.content.map((program) => program.id),
      ])

      this._isLoadingPrograms.set(false)
    }
  }

  addFilteredPrograms(programIds: string[]) {
    const existingFilters = this._filteredProgramPlanning.get()
    const filteredProgramsToAdd = programIds.filter((id) => !existingFilters.includes(id))

    this._filteredProgramPlanning.update((programs) => [...programs, ...filteredProgramsToAdd])
  }

  addNewFilteredProgram(programId: string) {
    this._filteredProgramPlanning.update((programs) => [...programs, programId])
  }

  removeFilteredProgram(programId: string) {
    this._filteredProgramPlanning.update((programs) => programs.filter((p) => p !== programId))
  }

  toggleSelectedProgram(programId: string) {
    const newArray = toggleInArray(this._filteredProgramPlanning.get(), (id) => id === programId, programId)
    this._filteredProgramPlanning.set([...newArray])
  }
}
